export const getProfile = () => {
  try {
    const saved = JSON.parse(localStorage.getItem("abhyas_profile") || "{}");
    return {
      name: saved.name || "Student",
      board: saved.board || "CBSE",
      klass: saved.klass || "10",
      subjects:
        saved.subjects?.length > 0
          ? saved.subjects
          : ["Mathematics", "Physics", "Chemistry", "Biology"],
    };
  } catch {
    return {
      name: "Student",
      board: "CBSE",
      klass: "10",
      subjects: ["Mathematics", "Physics", "Chemistry", "Biology"],
    };
  }
};

export const saveProfile = (patch) => {
  const prev = JSON.parse(localStorage.getItem("abhyas_profile") || "{}");
  localStorage.setItem("abhyas_profile", JSON.stringify({ ...prev, ...patch }));
};

export const DIFFICULTIES = ["Easy", "Medium", "Hard"];

export const QUESTIONS = {
  Mathematics: {
    "Quadratic Equations": [
      {
        q: "Find the roots of x² − 5x + 6 = 0.",
        hint: "Try factoring into (x − a)(x − b).",
        steps: ["Factor: x² − 5x + 6 = (x − 2)(x − 3)", "Set each factor to zero: x − 2 = 0 or x − 3 = 0"],
        answer: "x = 2 or x = 3",
      },
      {
        q: "What is the discriminant of 2x² − 4x + 3 = 0, and what does it tell you?",
        hint: "Discriminant D = b² − 4ac.",
        steps: ["Here a = 2, b = −4, c = 3", "D = (−4)² − 4(2)(3) = 16 − 24 = −8", "D < 0 means no real roots"],
        answer: "D = −8, so the equation has no real roots",
      },
      {
        q: "Find the sum of the roots of 3x² − 6x + 2 = 0.",
        hint: "Sum of roots = −b/a.",
        steps: ["Here a = 3, b = −6", "Sum = −(−6)/3"],
        answer: "Sum of roots = 2",
      },
      {
        q: "Solve x² = 49.",
        hint: "Take the square root of both sides — don't forget the negative root.",
        steps: ["x² = 49", "x = ±√49"],
        answer: "x = +7 or x = −7",
      },
      {
        q: "Find the product of the roots of x² + 7x + 10 = 0.",
        hint: "Product of roots = c/a.",
        steps: ["Here a = 1, c = 10", "Product = 10/1"],
        answer: "Product of roots = 10",
      },
    ],
    "Introduction to Trigonometry": [
      {
        q: "What is the value of sin²θ + cos²θ?",
        hint: "This is the fundamental Pythagorean identity.",
        steps: ["For any angle θ, sin²θ + cos²θ is constant", "It follows directly from the Pythagorean theorem on the unit circle"],
        answer: "1",
      },
      {
        q: "Find tan 45°.",
        hint: "tan θ = sin θ / cos θ, and sin 45° = cos 45°.",
        steps: ["sin 45° = cos 45° = 1/√2", "tan 45° = (1/√2) / (1/√2)"],
        answer: "tan 45° = 1",
      },
      {
        q: "If sin θ = 1/2 and θ is acute, find θ.",
        hint: "Recall the standard angle table for 30°, 45°, 60°.",
        steps: ["sin 30° = 1/2", "θ is acute, so the standard value applies directly"],
        answer: "θ = 30°",
      },
      {
        q: "What is cos 60°?",
        hint: "cos 60° equals sin 30°.",
        steps: ["cos 60° = sin (90° − 60°) = sin 30°", "sin 30° = 1/2"],
        answer: "cos 60° = 1/2",
      },
      {
        q: "Simplify sec²θ − tan²θ.",
        hint: "Divide the identity sin²θ + cos²θ = 1 by cos²θ.",
        steps: ["Dividing by cos²θ gives tan²θ + 1 = sec²θ", "Rearrange: sec²θ − tan²θ = 1"],
        answer: "1",
      },
    ],
    Integrals: [
      {
        q: "Evaluate ∫ x² dx.",
        hint: "Increase the power by 1, then divide by the new power.",
        steps: ["Power rule: ∫ xⁿ dx = xⁿ⁺¹/(n+1) + C", "Here n = 2, so we get x³/3"],
        answer: "x³/3 + C",
      },
      {
        q: "Evaluate ∫ (1/x) dx.",
        hint: "This is a standard result involving the logarithm.",
        steps: ["The derivative of ln|x| is 1/x", "So integration reverses it"],
        answer: "ln|x| + C",
      },
      {
        q: "Evaluate ∫ cos x dx.",
        hint: "Which function differentiates to cos x?",
        steps: ["d/dx (sin x) = cos x", "Therefore ∫ cos x dx = sin x"],
        answer: "sin x + C",
      },
      {
        q: "Evaluate ∫₀¹ 2x dx.",
        hint: "Integrate first, then substitute the limits.",
        steps: ["∫ 2x dx = x²", "Apply limits: (1)² − (0)² = 1"],
        answer: "1",
      },
      {
        q: "Evaluate ∫ eˣ dx.",
        hint: "eˣ is its own derivative.",
        steps: ["d/dx (eˣ) = eˣ", "So the integral is the same function"],
        answer: "eˣ + C",
      },
    ],
  },
  Physics: {
    "Describing Motion Around Us": [
      {
        q: "A car goes from rest to 20 m/s in 5 s. Find its acceleration.",
        hint: "a = (v − u) / t.",
        steps: ["u = 0, v = 20 m/s, t = 5 s", "a = (20 − 0) / 5"],
        answer: "a = 4 m/s²",
      },
      {
        q: "What is the SI unit of acceleration?",
        hint: "It's velocity change per second.",
        steps: ["Velocity is measured in m/s", "Acceleration = velocity ÷ time = (m/s) ÷ s"],
        answer: "m/s² (metres per second squared)",
      },
      {
        q: "A body starts at 10 m/s and accelerates at 2 m/s² for 3 s. Find its final velocity.",
        hint: "Use v = u + at.",
        steps: ["u = 10 m/s, a = 2 m/s², t = 3 s", "v = 10 + (2 × 3)"],
        answer: "v = 16 m/s",
      },
      {
        q: "A cyclist rides at 60 km/h for 2 hours. How far does she go?",
        hint: "Distance = speed × time.",
        steps: ["Speed = 60 km/h, time = 2 h", "Distance = 60 × 2"],
        answer: "120 km",
      },
      {
        q: "What is the approximate acceleration due to gravity near Earth's surface?",
        hint: "It's just under 10 m/s².",
        steps: ["Free-fall acceleration is denoted g", "Standard value used in Class 9–10 problems"],
        answer: "g ≈ 9.8 m/s²",
      },
    ],
    Electricity: [
      {
        q: "A current of 0.5 A flows through a 10 Ω resistor. Find the voltage.",
        hint: "Use Ohm's Law: V = IR.",
        steps: ["I = 0.5 A, R = 10 Ω", "V = 0.5 × 10"],
        answer: "V = 5 V",
      },
      {
        q: "What is the SI unit of electrical resistance?",
        hint: "It's named after a German physicist.",
        steps: ["Resistance R = V/I", "Its unit is named after Georg Simon Ohm"],
        answer: "Ohm (Ω)",
      },
      {
        q: "An appliance runs on 220 V and draws 2 A. Find its power.",
        hint: "P = V × I.",
        steps: ["V = 220 V, I = 2 A", "P = 220 × 2"],
        answer: "P = 440 W",
      },
      {
        q: "Two resistors of 2 Ω and 3 Ω are connected in series. Find the total resistance.",
        hint: "In series, resistances simply add.",
        steps: ["R(total) = R₁ + R₂", "R = 2 + 3"],
        answer: "5 Ω",
      },
      {
        q: "How much charge flows when a current of 2 A runs for 10 s?",
        hint: "Q = I × t.",
        steps: ["I = 2 A, t = 10 s", "Q = 2 × 10"],
        answer: "Q = 20 C (coulombs)",
      },
    ],
    "Light – Reflection and Refraction": [
      {
        q: "What is the focal length of a plane mirror?",
        hint: "A plane mirror has no curvature.",
        steps: ["Focal length depends on radius of curvature", "A plane surface has an infinite radius of curvature"],
        answer: "Infinity",
      },
      {
        q: "What is the approximate speed of light in vacuum?",
        hint: "It's 3 followed by 8 zeros, in m/s.",
        steps: ["Speed of light is denoted c", "c ≈ 3 × 10⁸ m/s"],
        answer: "c ≈ 3 × 10⁸ m/s",
      },
      {
        q: "Which type of mirror converges parallel rays of light to a point?",
        hint: "It curves inward, like the inside of a spoon.",
        steps: ["Concave mirrors reflect parallel rays toward a focus", "Convex mirrors diverge rays"],
        answer: "Concave mirror",
      },
      {
        q: "What causes light to bend when it passes from air into water?",
        hint: "Light travels at different speeds in different media.",
        steps: ["Light slows down in water", "The change in speed bends the ray — this is refraction"],
        answer: "Refraction (change in speed of light between media)",
      },
      {
        q: "A lens has a focal length of 0.5 m. Find its power.",
        hint: "P = 1/f, with f in metres.",
        steps: ["f = 0.5 m", "P = 1 / 0.5 = 2"],
        answer: "P = 2 D (dioptres)",
      },
    ],
  },
  Chemistry: {
    "Chemical Reactions and Equations": [
      {
        q: "Balance the equation: H₂ + O₂ → H₂O.",
        hint: "Count oxygen atoms on each side first.",
        steps: ["Put 2 before H₂O: H₂ + O₂ → 2H₂O", "Now hydrogen is unbalanced — put 2 before H₂"],
        answer: "2H₂ + O₂ → 2H₂O",
      },
      {
        q: "Rusting of iron is an example of which type of reaction?",
        hint: "Iron gains oxygen from moist air.",
        steps: ["Iron reacts with oxygen and water", "Gain of oxygen is oxidation"],
        answer: "Oxidation (a slow combination reaction)",
      },
      {
        q: "2Mg + O₂ → 2MgO is what type of reaction?",
        hint: "Two substances combine into one product.",
        steps: ["Two reactants form a single product", "This defines a combination reaction"],
        answer: "Combination reaction",
      },
      {
        q: "What is the colour of a copper sulphate (CuSO₄) solution?",
        hint: "It's a common bright colour in the chemistry lab.",
        steps: ["Cu²⁺ ions give the solution its colour", "Copper sulphate crystals dissolve to form a distinctly coloured solution"],
        answer: "Blue",
      },
      {
        q: "A reaction that releases heat is called what?",
        hint: "'Exo' means out.",
        steps: ["Heat is released to the surroundings", "Reactions releasing heat are exothermic"],
        answer: "Exothermic reaction",
      },
    ],
    "Acids, Bases and Salts": [
      {
        q: "What is the pH of a neutral solution at 25°C?",
        hint: "It's exactly in the middle of the pH scale.",
        steps: ["The pH scale runs from 0 to 14", "Neutral means neither acidic nor basic"],
        answer: "pH = 7",
      },
      {
        q: "Complete the reaction: HCl + NaOH → ?",
        hint: "An acid and a base cancel each other out.",
        steps: ["Acid + base → salt + water", "Here the salt formed is sodium chloride"],
        answer: "NaCl + H₂O (neutralisation)",
      },
      {
        q: "Is a solution with pH 4 acidic or basic?",
        hint: "Compare it with 7.",
        steps: ["pH below 7 means more H⁺ ions", "So the solution is acidic"],
        answer: "Acidic",
      },
      {
        q: "What happens when blue litmus paper is dipped in lemon juice?",
        hint: "Lemon juice contains citric acid.",
        steps: ["Lemon juice is acidic", "Acids turn blue litmus red"],
        answer: "Blue litmus turns red",
      },
      {
        q: "What is the chemical formula of baking soda?",
        hint: "It's a sodium compound of hydrogen carbonate.",
        steps: ["Baking soda is sodium hydrogen carbonate", "Na⁺ combines with HCO₃⁻"],
        answer: "NaHCO₃",
      },
    ],
    "Carbon and its Compounds": [
      {
        q: "What is the molecular formula of methane?",
        hint: "It's the simplest alkane — one carbon with hydrogens.",
        steps: ["Methane has 1 carbon atom", "Carbon forms 4 bonds, all with hydrogen"],
        answer: "CH₄",
      },
      {
        q: "What is the chemical formula of ethanol?",
        hint: "It's a 2-carbon alcohol.",
        steps: ["Ethanol has 2 carbons and an −OH group", "Structure: CH₃CH₂OH"],
        answer: "C₂H₅OH",
      },
      {
        q: "What is catenation?",
        hint: "It's the property that lets carbon form long chains.",
        steps: ["Carbon atoms bond strongly with each other", "This self-linking ability is called catenation"],
        answer: "The self-linking property of carbon atoms to form chains and rings",
      },
      {
        q: "What is the general formula of alkanes?",
        hint: "Each carbon gets roughly twice as many hydrogens, plus two.",
        steps: ["Alkanes have only single bonds", "For n carbons, hydrogens = 2n + 2"],
        answer: "CₙH₂ₙ₊₂",
      },
      {
        q: "Is ethene (C₂H₄) saturated or unsaturated? Why?",
        hint: "Look at the bond between the two carbons.",
        steps: ["Ethene has a carbon–carbon double bond", "Compounds with double or triple bonds are unsaturated"],
        answer: "Unsaturated — it contains a C=C double bond",
      },
    ],
  },
  Biology: {
    "Cell: The Building Block of Life": [
      {
        q: "Which organelle is called the powerhouse of the cell?",
        hint: "It produces ATP through respiration.",
        steps: ["Energy in cells is released as ATP", "This happens in the mitochondria"],
        answer: "Mitochondria",
      },
      {
        q: "What is the plant cell wall mainly made of?",
        hint: "It's the same fibre found in paper and cotton.",
        steps: ["The cell wall gives plants rigidity", "It is composed of a tough carbohydrate"],
        answer: "Cellulose",
      },
      {
        q: "Which part of the cell contains genetic material (DNA)?",
        hint: "It's the control centre of the cell.",
        steps: ["DNA carries hereditary information", "It is stored inside a membrane-bound organelle"],
        answer: "Nucleus",
      },
      {
        q: "What is osmosis?",
        hint: "It involves water moving across a membrane.",
        steps: ["Water moves from dilute to concentrated solution", "Movement happens through a semipermeable membrane"],
        answer: "Movement of water across a semipermeable membrane from low to high solute concentration",
      },
      {
        q: "Which cell organelle makes proteins?",
        hint: "They look like tiny dots, often on the rough ER.",
        steps: ["Proteins are assembled from amino acids", "This assembly happens at the ribosomes"],
        answer: "Ribosomes",
      },
    ],
    "Body Fluids and Circulation": [
      {
        q: "How many chambers does the human heart have?",
        hint: "Two on top, two below.",
        steps: ["The heart has 2 atria and 2 ventricles", "Total chambers = 2 + 2"],
        answer: "4 chambers",
      },
      {
        q: "Which blood cells carry oxygen, and using what pigment?",
        hint: "They're the most numerous cells in blood.",
        steps: ["Red blood cells transport oxygen", "They use the iron-rich pigment haemoglobin"],
        answer: "Red blood cells, using haemoglobin",
      },
      {
        q: "Do arteries carry blood towards or away from the heart?",
        hint: "Think of the aorta, the largest artery.",
        steps: ["The heart pumps blood out under pressure", "Vessels carrying blood away are arteries"],
        answer: "Away from the heart",
      },
      {
        q: "What is a normal resting heart rate for a teenager or adult?",
        hint: "It's around 70 beats per minute.",
        steps: ["Resting heart rate is measured in bpm", "The average is about 72 beats per minute"],
        answer: "About 72 beats per minute",
      },
      {
        q: "What is the function of valves in the heart and veins?",
        hint: "They stop blood from going backwards.",
        steps: ["Blood must flow in one direction", "Valves close to prevent backflow"],
        answer: "They prevent the backflow of blood",
      },
    ],
    Heredity: [
      {
        q: "What is the full form of DNA?",
        hint: "It's a nucleic acid with 'deoxyribo' sugar.",
        steps: ["DNA is the molecule of heredity", "Its name comes from its sugar, deoxyribose"],
        answer: "Deoxyribonucleic acid",
      },
      {
        q: "What is a gene?",
        hint: "It's a segment of DNA with a specific job.",
        steps: ["DNA is divided into functional units", "Each unit that codes for a trait is a gene"],
        answer: "A unit of heredity — a DNA segment that codes for a trait",
      },
      {
        q: "How many pairs of chromosomes do humans have?",
        hint: "The total count is 46.",
        steps: ["Humans have 46 chromosomes in total", "46 ÷ 2 = 23 pairs"],
        answer: "23 pairs",
      },
      {
        q: "Who is known as the father of genetics?",
        hint: "He experimented with pea plants in a monastery garden.",
        steps: ["His pea-plant experiments revealed inheritance patterns", "Gregor Mendel laid the foundation of genetics"],
        answer: "Gregor Johann Mendel",
      },
      {
        q: "In a heterozygote (Tt), which allele is expressed?",
        hint: "One allele masks the other.",
        steps: ["The dominant allele masks the recessive one", "So the trait of T (e.g. tallness) appears"],
        answer: "The dominant allele (T)",
      },
    ],
  },
};
